/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2022, Zumper
 */

import * as Api from '@zcode/nr/es/shared/api'
import { debounce } from '@zcode/utils'
import type { Dispatch } from 'redux'
import { logger } from '../../../logger'
import type { GetState } from '../../types'
import {
  receiveStreamChannels,
  setFetchedUnfilteredChannels,
  setFetchingStreamConversations,
  setFetchingUnfilteredChannels,
} from '../actions/index'
import { listingAndBuildingIdToTuple, selectFetchingUnfilteredChannels } from '../selectors/index'
import type { BuildingListingIdParams, ListingIdAndBuildingIdTuple } from '../types'

export type Params =
  | BuildingListingIdParams
  | { readonly buildingId?: undefined; readonly listingId?: undefined; readonly limit?: number }

const hasBuildingOrListingId = (params?: Params): params is BuildingListingIdParams => {
  return !!(params?.buildingId ?? params?.listingId)
}

const hasBuildingOrListingIds = (params?: Api.QueryConversationsParams) => {
  return !!(params?.building_ids?.length || params?.listing_ids?.length)
}

const pendingParams: ListingIdAndBuildingIdTuple[] = []

const queryStreamChannels = async (dispatch: Dispatch, params?: Api.QueryConversationsParams) => {
  let nextCursor: string | undefined
  let fetchedConversations = 0
  const hasFilters = hasBuildingOrListingIds(params)
  if (!hasFilters) {
    dispatch(setFetchingUnfilteredChannels(true))
  }
  try {
    do {
      const response = await Api.streamChannels.query({ ...params, cursor: nextCursor })
      const { conversations, limit } = response
      nextCursor = response.next_cursor ?? undefined
      fetchedConversations += conversations.length
      dispatch(receiveStreamChannels(conversations))
      if (!hasFilters) {
        dispatch(
          setFetchedUnfilteredChannels({
            fetchedAll: conversations.length < limit,
            fetchedAt: Date.now(),
          })
        )
      }
      // eslint-disable-next-line no-unmodified-loop-condition
    } while (nextCursor && (hasFilters || (params?.limit && fetchedConversations < params.limit)))
  } catch (e) {
    if (!hasFilters) {
      dispatch(
        setFetchedUnfilteredChannels({
          fetchedAll: false,
          fetchedAt: Date.now(),
        })
      )
    }
    throw e
  }
}

const debounced = debounce(async (dispatch: Dispatch) => {
  const ids = pendingParams.slice()
  pendingParams.length = 0
  try {
    const buildingIds = new Set<number>()
    const listingIds = new Set<number>()
    for (const [listingId, buildingId] of ids) {
      if (buildingId) {
        buildingIds.add(buildingId)
      }
      if (listingId) {
        listingIds.add(listingId)
      }
    }
    await queryStreamChannels(dispatch, {
      building_ids: Array.from(buildingIds),
      listing_ids: Array.from(listingIds),
    })
  } catch (e) {
    logger.error('Failed to query stream conversations', e)
  } finally {
    dispatch(setFetchingStreamConversations({ fetching: false, ids }))
  }
}, 50)

export const loadStreamConversations =
  (params?: Params) => async (dispatch: Dispatch, getState: GetState) => {
    try {
      if (hasBuildingOrListingId(params)) {
        const ids = listingAndBuildingIdToTuple(params)
        dispatch(setFetchingStreamConversations({ fetching: true, ids: [ids] }))
        pendingParams.push(ids)
        debounced(dispatch)
      } else if (!selectFetchingUnfilteredChannels(getState())) {
        await queryStreamChannels(dispatch, params)
      }
    } catch (e) {
      logger.error('Failed to query stream conversations', e)
    }
  }
